import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { App, Card, Space, Tag, Typography, theme } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { updateStageData } from "../../lib/api";
import type {
  RiskAssessment,
  RiskDataClassification,
  RiskHarmLevel,
  RiskLevel,
  WorkflowStage,
} from "../intakeDetails/types";

interface Props {
  workflowId: string;
  stage: WorkflowStage;
  /** Called after a successful save so the parent can refetch and re-evaluate
   *  the Advance button's enabled state. */
  onSaved?: () => void;
}

const HARM_LEVELS: RiskHarmLevel[] = ["Minor", "Moderate", "Major", "Severe"];
const DATA_CLASSIFICATIONS: RiskDataClassification[] = [
  "Public",
  "Internal",
  "Confidential",
  "Restricted",
];

// Mirrors functions/domains/workflows.py RISK_MATRIX. Kept client-side for
// instant feedback; the server recomputes on save and is the source of truth.
const RISK_MATRIX: Record<RiskDataClassification, Record<RiskHarmLevel, RiskLevel>> = {
  Public: { Minor: "Low", Moderate: "Low", Major: "High", Severe: "Very High" },
  Internal: { Minor: "Low", Moderate: "Medium", Major: "High", Severe: "Very High" },
  Confidential: { Minor: "Medium", Moderate: "High", Major: "High", Severe: "Very High" },
  Restricted: { Minor: "High", Moderate: "High", Major: "Very High", Severe: "Very High" },
};

const RISK_TAG_COLOR: Record<RiskLevel, string> = {
  Low: "green",
  Medium: "gold",
  High: "orange",
  "Very High": "red",
};

// Background fill used for matrix cells (and tags) per risk level. Subtle
// enough to keep cell text readable without breaking DIT contrast rules.
const RISK_CELL_BG: Record<RiskLevel, string> = {
  Low: "#f6ffed",
  Medium: "#fffbe6",
  High: "#fff7e6",
  "Very High": "#fff1f0",
};

interface LevelDef<T extends string> {
  level: T;
  description: string;
  example: string;
}

// All rubric copy lifted verbatim from
// ~/Downloads/2 - AI Use-Case Risk Definition.docx so staff see the same
// wording the agency used to draft the policy.
const HARM_TO_AGENCY: LevelDef<RiskHarmLevel>[] = [
  {
    level: "Severe",
    description:
      "Major disruption to core service delivery including total failure of mission-essential services; massive data breach of PII or PHI. Requires emergency funding, external consultants, and potentially federal intervention to remediate. Clear violation of laws, regulations, or state governance requirements. Complete reputational loss with years-long negative coverage.",
    example:
      "AI-managed emergency dispatch system fails during a crisis, resulting in a total loss of response capabilities.",
  },
  {
    level: "Major",
    description:
      "Substantial disruption to core services and departmental workflows. Significant additional cost or resource burden to remediate. Serious regulatory violations; high-profile lawsuits; formal audits by the State Auditor or AG. Severe reputational loss and/or negative mainstream news coverage for a prolonged duration (≥2 weeks).",
    example:
      "An automated tax processing system incorrectly calculates withholdings for all state employees, requiring months of manual reconciliation.",
  },
  {
    level: "Moderate",
    description:
      "Noticeable operational inefficiency (e.g., slower processing, service downtime). Moderate cost or resource burden, with brief reputational loss and/or negative media coverage (<2 weeks). Procedural or partial noncompliance with laws.",
    example:
      "AI scheduling tool creates widespread double bookings, causing a 2-week appointment backlog and requiring overtime to resolve.",
  },
  {
    level: "Minor",
    description:
      "Minimal operational disruption; small process error or inefficiency easily corrected with existing workflows. No reputational harm; minor compliance issues resolved internally without penalties or formal review.",
    example:
      "Minor data formatting error in an internal AI report, quickly fixed by staff with no service delay.",
  },
];

const HARM_TO_PUBLIC: LevelDef<RiskHarmLevel>[] = [
  {
    level: "Severe",
    description:
      "Direct harm to North Carolinians through systematic, erroneous denial of fundamental rights, liberties, or life-sustaining benefits (e.g., total loss of healthcare coverage or unemployment insurance). Permanent, irreversible exposure of highly sensitive personal data for a significant portion of the population. Absolute loss of institutional credibility; potential civil-rights investigations or class-action litigation against the state.",
    example:
      "An automated Medicaid eligibility system contains a logic error that erroneously terminates coverage for 50,000 vulnerable residents.",
  },
  {
    level: "Major",
    description:
      "Significant financial hardship or loss of access to essential services (healthcare, SNAP) for a specific group, especially vulnerable populations. Documented algorithmic bias that prevents equitable service delivery. Significant loss of public trust in government technology or perceived unfair treatment, potentially resulting in social or political backlash.",
    example:
      "Unintended bias in the model results in discrimination against senior North Carolinians during benefits eligibility review.",
  },
  {
    level: "Moderate",
    description:
      "Potential for unfair or inaccurate outcomes affecting the public, including vulnerable populations. Lack of clarity on how a decision was made, leading to increased public skepticism. Impact is reversible but requires intervention to re-establish baseline.",
    example:
      "AI chatbot provides incomplete or misleading answers to benefits applicants, requiring employee follow-up.",
  },
  {
    level: "Minor",
    description:
      "Negligible impact on the public; issue limited to internal users or a very small group of individuals. No loss of trust or fairness concerns; outcomes remain accurate. Impact easily mitigated.",
    example:
      "AI chatbot provides a delayed response or minor typo in an informational query, causing no confusion or harm.",
  },
];

const DATA_CLASS_DEFS: LevelDef<RiskDataClassification>[] = [
  {
    level: "Restricted",
    description:
      "Highest level of sensitivity and presents the highest risk if disclosed. Access is restricted to a limited audience. Disclosure possible only under very specific conditions.",
    example:
      "PII, PCI/payment data, PHI/HIPAA, criminal-justice information, state and federal tax information, SSA-provided information.",
  },
  {
    level: "Confidential",
    description:
      "Often limited to a small audience on a need-to-know basis. Not for general internal circulation.",
    example:
      "Business unit plans, security features, sensitive public-security information, draft annual/quarterly reports.",
  },
  {
    level: "Internal",
    description:
      "Typically used within the agency and not for public sharing. Most documents are classified Internal, and most employees would have access.",
    example:
      "Employee work schedules, administrative records, internal newsletters, draft policies, personal email/home addresses.",
  },
  {
    level: "Public",
    description:
      "Available and intended for public access. Already accessible through public sources.",
    example:
      "Publicly accessible website content, routine correspondence, employee compensation, final press releases, job postings, published policies and standards, work email addresses.",
  },
];

function deriveOverallHarm(
  agency: RiskHarmLevel | undefined,
  publicHarm: RiskHarmLevel | undefined,
): RiskHarmLevel | undefined {
  if (!agency || !publicHarm) return undefined;
  return HARM_LEVELS.indexOf(agency) >= HARM_LEVELS.indexOf(publicHarm) ? agency : publicHarm;
}

function deriveRiskLevel(
  agency: RiskHarmLevel | undefined,
  publicHarm: RiskHarmLevel | undefined,
  dataClass: RiskDataClassification | undefined,
): RiskLevel | undefined {
  const overall = deriveOverallHarm(agency, publicHarm);
  if (!overall || !dataClass) return undefined;
  return RISK_MATRIX[dataClass][overall];
}

interface RubricGroupProps<T extends string> {
  name: string;
  description: string;
  levels: LevelDef<T>[];
  value: T | undefined;
  disabled?: boolean;
  onChange: (value: T) => void;
}

function RubricGroup<T extends string>({
  name,
  description,
  levels,
  value,
  disabled,
  onChange,
}: RubricGroupProps<T>) {
  const { token } = theme.useToken();

  const handleKey = (event: KeyboardEvent<HTMLDivElement>, level: T) => {
    if (disabled) return;
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      onChange(level);
    }
  };

  return (
    <div role="radiogroup" aria-label={name}>
      <Typography.Text strong style={{ display: "block" }}>
        {name}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        {description}
      </Typography.Text>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        {levels.map((opt) => {
          const selected = opt.level === value;
          return (
            <div
              key={opt.level}
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && onChange(opt.level)}
              onKeyDown={(event) => handleKey(event, opt.level)}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                padding: 12,
                borderRadius: token.borderRadius,
                border: `2px solid ${selected ? token.colorPrimary : token.colorBorderSecondary}`,
                background: selected ? token.colorPrimaryBg : token.colorBgContainer,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                transition: "border-color 120ms, background 120ms",
              }}
            >
              <span
                aria-hidden
                style={{
                  marginTop: 2,
                  width: 18,
                  height: 18,
                  flex: "0 0 18px",
                  borderRadius: "50%",
                  border: `2px solid ${selected ? token.colorPrimary : token.colorBorder}`,
                  background: selected ? token.colorPrimary : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selected ? (
                  <CheckCircleFilled style={{ color: token.colorBgContainer, fontSize: 12 }} />
                ) : null}
              </span>
              <div style={{ flex: 1 }}>
                <Typography.Text strong>{opt.level}</Typography.Text>
                <Typography.Paragraph style={{ marginBottom: 4, marginTop: 2 }}>
                  {opt.description}
                </Typography.Paragraph>
                <Typography.Text type="secondary" italic style={{ fontSize: 13 }}>
                  Example: {opt.example}
                </Typography.Text>
              </div>
            </div>
          );
        })}
      </Space>
    </div>
  );
}

interface RiskMatrixProps {
  overallHarm: RiskHarmLevel | undefined;
  dataClass: RiskDataClassification | undefined;
}

function RiskMatrixGrid({ overallHarm, dataClass }: RiskMatrixProps) {
  const { token } = theme.useToken();
  const headerStyle = {
    padding: "8px 10px",
    fontWeight: 600,
    background: token.colorFillTertiary,
    border: `1px solid ${token.colorBorderSecondary}`,
    textAlign: "center" as const,
    fontSize: 13,
  };
  const rowHeaderStyle = {
    ...headerStyle,
    textAlign: "left" as const,
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        aria-label="Risk Matrix"
        style={{
          borderCollapse: "collapse",
          width: "100%",
          minWidth: 460,
        }}
      >
        <caption
          style={{
            captionSide: "top",
            textAlign: "left",
            padding: "0 0 8px 0",
            fontWeight: 600,
          }}
        >
          Risk Matrix — Data Classification × Potential for Harm
        </caption>
        <thead>
          <tr>
            <th style={headerStyle} aria-hidden />
            {HARM_LEVELS.map((harm) => (
              <th key={harm} scope="col" style={headerStyle}>
                {harm}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DATA_CLASSIFICATIONS.map((dc) => (
            <tr key={dc}>
              <th scope="row" style={rowHeaderStyle}>
                {dc}
              </th>
              {HARM_LEVELS.map((harm) => {
                const cellRisk = RISK_MATRIX[dc][harm];
                const isChosen = dc === dataClass && harm === overallHarm;
                return (
                  <td
                    key={harm}
                    style={{
                      padding: "10px",
                      textAlign: "center",
                      border: isChosen
                        ? `2px solid ${token.colorPrimary}`
                        : `1px solid ${token.colorBorderSecondary}`,
                      background: RISK_CELL_BG[cellRisk],
                      fontWeight: isChosen ? 700 : 400,
                      fontSize: 13,
                    }}
                    aria-current={isChosen ? "true" : undefined}
                  >
                    {cellRisk}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Risk Assessment panel — sits inside the scoring_chart stage. Three rubric
 * questions (Harm to Agency, Harm to Public, Data Classification) rendered as
 * stacked cards with the full rubric definition + example inline so staff
 * read the same wording the policy doc uses. After all three are picked, a
 * 4×4 matrix appears with the chosen cell highlighted. Required to advance
 * past scoring (gate enforced both client- and server-side; OAIP-91).
 *
 * Source rubric: ~/Downloads/2 - AI Use-Case Risk Definition.docx (3 tables).
 */
export default function RiskAssessmentPanel({ workflowId, stage, onSaved }: Props) {
  const { message } = App.useApp();
  const initial: Partial<RiskAssessment> = stage.riskAssessment ?? {};
  const [harmAgency, setHarmAgency] = useState<RiskHarmLevel | undefined>(initial.harmToAgency);
  const [harmPublic, setHarmPublic] = useState<RiskHarmLevel | undefined>(initial.harmToPublic);
  const [dataClass, setDataClass] = useState<RiskDataClassification | undefined>(
    initial.dataClassification,
  );
  const [saving, setSaving] = useState(false);

  // Re-sync from props when the parent refetches the workflow bundle.
  useEffect(() => {
    setHarmAgency(stage.riskAssessment?.harmToAgency);
    setHarmPublic(stage.riskAssessment?.harmToPublic);
    setDataClass(stage.riskAssessment?.dataClassification);
  }, [
    stage.riskAssessment?.harmToAgency,
    stage.riskAssessment?.harmToPublic,
    stage.riskAssessment?.dataClassification,
  ]);

  const overallHarm = useMemo(() => deriveOverallHarm(harmAgency, harmPublic), [harmAgency, harmPublic]);
  const derivedRisk = useMemo(
    () => deriveRiskLevel(harmAgency, harmPublic, dataClass),
    [harmAgency, harmPublic, dataClass],
  );

  const persistIfComplete = async (
    nextAgency: RiskHarmLevel | undefined,
    nextPublic: RiskHarmLevel | undefined,
    nextClass: RiskDataClassification | undefined,
  ) => {
    if (!nextAgency || !nextPublic || !nextClass) return;
    setSaving(true);
    try {
      await updateStageData(workflowId, stage.id, {
        riskAssessment: {
          harmToAgency: nextAgency,
          harmToPublic: nextPublic,
          dataClassification: nextClass,
        },
      });
      onSaved?.();
    } catch (error) {
      // Roll back optimistic state to whatever the server had.
      setHarmAgency(stage.riskAssessment?.harmToAgency);
      setHarmPublic(stage.riskAssessment?.harmToPublic);
      setDataClass(stage.riskAssessment?.dataClassification);
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const allComplete = Boolean(harmAgency && harmPublic && dataClass);

  return (
    <Card
      size="small"
      title="Risk Assessment"
      extra={
        derivedRisk ? (
          <Tag color={RISK_TAG_COLOR[derivedRisk]} style={{ fontSize: 14 }}>
            Risk Level: {derivedRisk}
          </Tag>
        ) : (
          <Typography.Text type="secondary">Risk Level: pending</Typography.Text>
        )
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Required before advancing past Fill Scoring Chart. Imagine the
          worst-case scenario for an AI failure and pick the level that best
          matches. Risk Level is auto-derived from the matrix in the AI
          Use-Case Risk Definition document.
        </Typography.Text>

        <RubricGroup
          name="Step 1a — Potential for Harm to the Agency (Internal)"
          description="Operational, financial, reputational, mission interruption."
          levels={HARM_TO_AGENCY}
          value={harmAgency}
          disabled={saving}
          onChange={(level) => {
            setHarmAgency(level);
            persistIfComplete(level, harmPublic, dataClass);
          }}
        />

        <RubricGroup
          name="Step 1b — Potential for Harm to the Public (External)"
          description="Trust, fairness, public service access, equity, responsibility."
          levels={HARM_TO_PUBLIC}
          value={harmPublic}
          disabled={saving}
          onChange={(level) => {
            setHarmPublic(level);
            persistIfComplete(harmAgency, level, dataClass);
          }}
        />

        {overallHarm ? (
          <Typography.Text>
            <strong>Overall potential for harm</strong> (higher of the two):{" "}
            <Tag style={{ marginInlineStart: 4 }}>{overallHarm}</Tag>
          </Typography.Text>
        ) : null}

        <RubricGroup
          name="Step 2 — Data Sensitivity / Classification"
          description="Pick the highest classification this use case will touch."
          levels={DATA_CLASS_DEFS}
          value={dataClass}
          disabled={saving}
          onChange={(level) => {
            setDataClass(level);
            persistIfComplete(harmAgency, harmPublic, level);
          }}
        />

        <div>
          <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
            Step 3 — Risk Matrix
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Maps Data Classification × Overall Potential for Harm to a final
            Risk Level. Your chosen cell will highlight once all three values
            above are picked.
          </Typography.Text>
          <RiskMatrixGrid overallHarm={overallHarm} dataClass={dataClass} />
        </div>

        {!allComplete ? (
          <Typography.Text type="warning">
            Pick all three values above to compute the Risk Level and unlock
            Advance.
          </Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
}
